import { motion } from "framer-motion";
import { Brain, Compass, Search, TrendingUp, Shield } from "lucide-react";
import { PROCESS } from "@/constants/testIds";

const steps = [
  {
    icon: Brain,
    title: "Understand your behaviour",
    desc: "We start by listening — to your fears, your dreams, your past financial mistakes. Behaviour is 80% of the outcome.",
  },
  {
    icon: Compass,
    title: "Define your goals",
    desc: "Retirement, child's education, a home, a sabbatical — every goal gets a time horizon and a number.",
  },
  {
    icon: Search,
    title: "Research & shortlist schemes",
    desc: "We compare schemes against their benchmark index, study the fund manager's track record, expense ratios and consistency.",
  },
  {
    icon: TrendingUp,
    title: "Construct & monitor portfolio",
    desc: "A diversified mix matched to your risk profile — reviewed every quarter, rebalanced when life changes.",
  },
  {
    icon: Shield,
    title: "Coach through every cycle",
    desc: "The real value of an advisor: stopping you from selling at the bottom or chasing fads at the top.",
  },
];

export default function ProcessTimeline() {
  return (
    <section
      id="process"
      data-testid={PROCESS.section}
      className="bg-muted/40 py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <span className="text-xs uppercase tracking-[0.25em] text-secondary font-medium">
              Why an advisor
            </span>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-primary mt-3 leading-[1.1]">
              Our research <em className="not-italic text-secondary">process</em>.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-lg text-foreground/75 leading-relaxed">
              Anyone can buy a mutual fund. An advisor does the part most investors skip — research,
              shortlisting, behavioural coaching. Here's the structure we follow with every client.
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="hidden md:block absolute left-1/2 top-4 bottom-4 w-px bg-primary/15 -translate-x-1/2"></div>

          <ol className="space-y-12 md:space-y-20">
            {steps.map((s, i) => {
              const isLeft = i % 2 === 0;
              return (
                <motion.li
                  key={s.title}
                  data-testid={PROCESS.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6 }}
                  className={`relative grid md:grid-cols-2 gap-8 items-center ${
                    isLeft ? "" : "md:[direction:rtl]"
                  }`}
                >
                  <div className={`${isLeft ? "md:text-right md:pr-12" : "md:[direction:ltr] md:pl-12"}`}>
                    <span className="font-serif text-secondary text-sm tracking-widest">
                      STEP 0{i + 1}
                    </span>
                    <h3 className="font-serif text-2xl md:text-3xl text-primary mt-2">{s.title}</h3>
                    <p className="text-foreground/75 mt-3 leading-relaxed">{s.desc}</p>
                  </div>

                  <div className="md:[direction:ltr] relative">
                    <div className="hidden md:block absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-secondary border-4 border-background"></div>
                    <div className={`bg-card rounded-2xl p-6 border border-border shadow-sm ${isLeft ? "md:ml-12" : "md:mr-12"}`}>
                      <div className="w-12 h-12 rounded-xl bg-primary/10 grid place-items-center text-primary mb-3">
                        <s.icon className="w-5 h-5" />
                      </div>
                      <p className="text-sm text-foreground/65 leading-relaxed">
                        We don't just sell schemes. We coach behaviour, set structure and stay accountable.
                      </p>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
